/**
 * Where the SPA reaches the admin API. The app's entire configuration, in one value.
 *
 * Read here rather than inside `createFetchClient` so the client stays a pure adapter —
 * it takes a `baseUrl` and knows nothing about Vite — and so this invariant has one place
 * to live.
 */
/**
 * `/api/v2`, and the **`/v2` is not optional**.
 *
 * `zapio-backend` sets `GLOBAL_PREFIX = 'api/v2'`, so every route lives under it. The
 * version is `v2` because the legacy services still own `/api/v1` and nginx routes that
 * prefix to them — pointing this at `v1` reaches the old backend, which has no admin API
 * at all. Two things break at once if this is `/api`, and the second is the one that
 * wastes an afternoon:
 *
 * 1. Every request 404s, because `/api/admin/auth/login` is not a route.
 * 2. **The session cookie would not be sent even if it were.** The access cookie is scoped
 *    `Path=/api/v2/admin`, and a browser sends a cookie only to paths under its `Path` —
 *    so a request to `/api/admin/…` carries no credential, and the fix would look like an
 *    authentication problem rather than a prefix one.
 *
 * The SPA and the API are same-origin by requirement — nginx serves this app and proxies
 * `/api` to the backend, and the Vite dev server does the same — so this is a property of
 * the deployment shape rather than a choice. It stays overridable, and the check below is
 * unaffected: the dangerous value is an absolute URL, not an absent one.
 */
const DEFAULT_BASE = '/api/v2'

export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE
  const base = typeof configured === 'string' && configured !== '' ? configured : DEFAULT_BASE

  /**
   * It must be a **path**, never an absolute URL, and this check is not pedantry.
   *
   * `zapio-backend` sets the session as two `httpOnly`, `SameSite=Strict` cookies, and
   * this client sends credentials `same-origin`. An absolute `VITE_API_BASE` therefore
   * produces requests that carry no cookie at all — every one answers 401, the SPA
   * redirects to the login screen, the login succeeds, and the next request 401s again. An
   * infinite sign-in loop with nothing in any log to explain it, from one value in a
   * `.env` file.
   *
   * The backend's CSRF check compounds it: it compares the browser's `Origin` against
   * `ADMIN_PANEL_URL`, so a cross-origin SPA also 403s on every write.
   *
   * Failing loudly at boot is the alternative.
   */
  if (/^[a-z][a-z0-9+.-]*:/i.test(base) || base.startsWith('//')) {
    throw new Error(
      `VITE_API_BASE must be a path such as '/api', not an absolute URL (got '${base}'). ` +
        'The session cookies are SameSite=Strict, so a cross-origin base sends no cookie ' +
        'and every request answers 401 — which presents as an endless sign-in loop. ' +
        'nginx serves this SPA and proxies /api to zapio-backend on ONE origin; the Vite ' +
        'dev server does the same.',
    )
  }

  return base.startsWith('/') ? base : `/${base}`
}
